import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { LuCheck, LuChevronDown, LuSearch } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';
import Avatar from './Avatar';

const normalize = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Card-style single select with a searchable popover (Insights project selector).
 * Trigger: avatar + small uppercase label + selected label. Options: { value, label, subtitle? }.
 * Keyboard: ArrowUp/ArrowDown move, Enter picks, Escape closes.
 */
const CardSelect = ({
  value,
  onChange,
  options = [],
  label = '',
  placeholder = 'Sélectionner…',
  searchPlaceholder = 'Rechercher…',
  sectionLabel = null,
  emptyLabel = 'Aucun résultat',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return options;
    return options.filter((o) => normalize(`${o.label} ${o.subtitle || ''}`).includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    const selected = options.findIndex((o) => o.value === value?.value);
    setCursor(selected >= 0 ? selected : 0);
    return () => {
      document.removeEventListener('mousedown', onDown);
      clearTimeout(t);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (opt) => {
    if (!opt) return;
    onChange?.(opt);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(filtered[cursor]);
    }
  };

  return (
    <div ref={rootRef} className={cx('relative', className)} onKeyDown={open ? onKeyDown : undefined}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        className={cx(
          'flex w-full cursor-pointer items-center gap-2.5 rounded-xl border bg-white px-2.5 py-2 text-left outline-none transition-colors dark:bg-night-800',
          'focus-visible:ring-2 focus-visible:ring-brand-500',
          open
            ? 'border-brand-600 ring-1 ring-brand-600/30 dark:border-brand-400'
            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600',
        )}
      >
        {value ? <Avatar text={value.label} /> : <span className="inline-flex h-9 w-9 flex-shrink-0 rounded-lg bg-gray-100 dark:bg-gray-700" aria-hidden />}
        <span className="min-w-0 flex-1">
          {label && <span className="mb-0.5 block text-[10px] font-bold uppercase leading-none tracking-wider text-gray-400 dark:text-gray-500">{label}</span>}
          <span className={cx('block truncate text-sm font-semibold', value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500')}>
            {value ? value.label : placeholder}
          </span>
        </span>
        <LuChevronDown className={cx('h-4 w-4 flex-shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-full rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl dark:border-gray-700 dark:bg-night-750">
          <div className="relative mb-1.5">
            <LuSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-controls={listId}
              aria-activedescendant={filtered[cursor] ? `${listId}-${cursor}` : undefined}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-8 pr-2 text-sm text-gray-900 outline-none focus:border-brand-600 dark:border-gray-700 dark:bg-night-900 dark:text-white dark:focus:border-brand-400"
            />
          </div>

          {sectionLabel && (
            <div className="px-2 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{sectionLabel}</div>
          )}

          <div id={listId} role="listbox" aria-label={label || placeholder} className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-2 py-3 text-center text-sm text-gray-400 dark:text-gray-500">{emptyLabel}</div>
            ) : (
              filtered.map((opt, index) => {
                const active = value && value.value === opt.value;
                return (
                  <button
                    key={opt.value}
                    id={`${listId}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => pick(opt)}
                    onMouseEnter={() => setCursor(index)}
                    className={cx(
                      'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors',
                      active ? 'bg-brand-50 dark:bg-brand-900' : index === cursor ? 'bg-gray-50 dark:bg-white/5' : '',
                    )}
                  >
                    <Avatar text={opt.label} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className={cx('block truncate text-sm font-semibold', active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-900 dark:text-white')}>
                        {opt.label}
                      </span>
                      {opt.subtitle && <span className="block truncate text-xs text-gray-400 dark:text-gray-500">{opt.subtitle}</span>}
                    </span>
                    {active && <LuCheck className="h-4 w-4 flex-shrink-0 text-brand-600 dark:text-brand-400" aria-hidden />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CardSelect;
